import React, { useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Save,
  Loader2,
  LayoutDashboard,
  FileText,
  Gavel,
  FolderOpen,
  TrendingUp,
  BookOpen,
  GraduationCap,
  Settings,
  Info,
  Users,
  Landmark,
  Gauge,
  Bot,
  BarChart3,
  Building2,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import {
  AGENCY_TYPES,
  getAgencyTypeLabel,
  getDefaultPortalAccess,
  getPortalSectionsForAgency,
  getTypedPortalAccess,
  isFundGeniusAgency,
  normalizeAgencyType,
} from '../../../utils/agencyType';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const SECTION_ICONS = {
  dashboard: LayoutDashboard,
  credit_report: FileText,
  dispute_wizard: Gavel,
  my_disputes: FolderOpen,
  my_documents: FolderOpen,
  credit_builder: TrendingUp,
  blueprints: BookOpen,
  academy: GraduationCap,
  settings: Settings,
  funding_clients: Users,
  client_details: Building2,
  applications: FileText,
  documents: FolderOpen,
  lenders: Landmark,
  readiness: Gauge,
  ai_advisor: Bot,
  reports: BarChart3,
};

const ACCESS_MODES = [
  { value: 'hidden', label: 'Hidden', icon: EyeOff, color: 'text-slate-400', bg: 'bg-slate-100' },
  { value: 'locked', label: 'Locked', icon: Lock, color: 'text-amber-600', bg: 'bg-amber-100' },
  { value: 'read_only', label: 'Read Only', icon: Eye, color: 'text-blue-600', bg: 'bg-blue-100' },
  { value: 'full_access', label: 'Full Access', icon: Unlock, color: 'text-emerald-600', bg: 'bg-emerald-100' },
];

/**
 * Portal Access Section
 *
 * Module list switches by agency type (credit_repair vs fundgenius).
 * Saves into the matching settings.portal_access bucket so types never overwrite each other.
 */
const PortalAccessSection = ({ agency, agencyId, isReadOnly, onUpdate }) => {
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);

  const agencyType = useMemo(() => normalizeAgencyType(agency), [agency]);
  const portalSections = useMemo(() => getPortalSectionsForAgency(agencyType), [agencyType]);
  const isFundGenius = agencyType === AGENCY_TYPES.FUNDGENIUS;

  const [accessConfig, setAccessConfig] = useState(() => getTypedPortalAccess(agency));

  React.useEffect(() => {
    setAccessConfig(getTypedPortalAccess(agency));
  }, [agency, agencyType]);

  const handleAccessChange = (sectionKey, value) => {
    setAccessConfig((prev) => ({ ...prev, [sectionKey]: value }));
  };

  const handleSave = async () => {
    if (isReadOnly) return;

    setSaving(true);
    try {
      const payloadSections = {};
      portalSections.forEach((section) => {
        payloadSections[section.key] = accessConfig[section.key] || 'full_access';
      });

      const response = await axios.patch(
        `${API}/admin/agencies/${agencyId}/portal-access`,
        {
          portal_access: payloadSections,
          agency_type: agencyType,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success('Portal access settings saved');
      if (onUpdate) {
        const nextTyped = response.data?.portal_access || payloadSections;
        const nextSettings = {
          ...(agency?.settings || {}),
          portal_access: {
            ...(agency?.settings?.portal_access || {}),
            [agencyType]: nextTyped,
          },
        };
        onUpdate({
          settings: nextSettings,
          ...(agencyType === AGENCY_TYPES.CREDIT_REPAIR ? { portal_access: nextTyped } : {}),
        });
      }
    } catch (err) {
      console.error('Failed to save portal access:', err);
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const getAccessMode = (value) => ACCESS_MODES.find((m) => m.value === value) || ACCESS_MODES[0];

  return (
    <div className="space-y-6" data-testid="portal-access-section" data-agency-type={agencyType}>
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
            <Shield className="text-indigo-600" size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Portal Access</h2>
            <p className="text-sm text-slate-500">
              {isFundGenius
                ? 'Configure FundGenius workspace modules for this agency'
                : 'Configure which portal sections are available to agency users'}
            </p>
          </div>
          <Badge className={`ml-auto border-0 ${isFundGenius ? 'bg-cyan-100 text-cyan-800' : 'bg-amber-100 text-amber-800'}`}>
            {getAgencyTypeLabel(agencyType)}
          </Badge>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="text-amber-600 mt-0.5" size={18} />
        <div className="text-sm text-amber-800">
          <p className="font-medium">Agency-Level Access Ceiling</p>
          <p className="text-amber-700 mt-1">
            These settings define the maximum access level for all users in this agency.
            Individual user permissions cannot exceed these levels.
            {isFundGenius
              ? ' FundGenius and Credit Repair module settings are stored separately.'
              : ''}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        {ACCESS_MODES.map((mode) => {
          const Icon = mode.icon;
          return (
            <div key={mode.value} className="flex items-center gap-2">
              <Badge className={`${mode.bg} ${mode.color} border-0`}>
                <Icon size={12} className="mr-1" />
                {mode.label}
              </Badge>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                Portal Section
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                Description
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase w-48">
                Access Level
              </th>
            </tr>
          </thead>
          <tbody>
            {portalSections.map((section) => {
              const Icon = SECTION_ICONS[section.key] || LayoutDashboard;
              const currentMode = getAccessMode(accessConfig[section.key] || 'full_access');
              const ModeIcon = currentMode.icon;

              return (
                <tr key={section.key} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`portal-section-${section.key}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Icon size={16} className="text-slate-600" />
                      </div>
                      <span className="font-medium text-slate-900">{section.label}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">
                    {section.description}
                  </td>
                  <td className="px-4 py-3">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <Select
                              value={accessConfig[section.key] || 'full_access'}
                              onValueChange={(value) => handleAccessChange(section.key, value)}
                              disabled={isReadOnly}
                            >
                              <SelectTrigger className={`w-40 bg-white ${currentMode.color}`}>
                                <div className="flex items-center gap-2">
                                  <ModeIcon size={14} />
                                  <SelectValue />
                                </div>
                              </SelectTrigger>
                              <SelectContent className="bg-white">
                                {ACCESS_MODES.map((mode) => {
                                  const MIcon = mode.icon;
                                  return (
                                    <SelectItem key={mode.value} value={mode.value}>
                                      <div className="flex items-center gap-2">
                                        <MIcon size={14} className={mode.color} />
                                        <span>{mode.label}</span>
                                      </div>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{currentMode.label}: {
                            currentMode.value === 'hidden' ? 'Section is completely hidden from users' :
                              currentMode.value === 'locked' ? 'Section is visible but not accessible' :
                                currentMode.value === 'read_only' ? 'Users can view but not modify' :
                                  'Full read and write access'
                          }</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!isReadOnly && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="portal-access-save"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export { getDefaultPortalAccess, isFundGeniusAgency };
export default PortalAccessSection;
